// The Network card's Entry routes (spec 11 6.15.28): each of the deployment's origin rows with its access and state,
// linking to the network's Entry points tab; a route waiting for the deployment's apply says so (warning tone).
import { Globe, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import type { Deployment, DeploymentOrigin } from '../../api/types';
import { ActionButton } from '../../components/ui';
import { strings } from '../../strings';

const e = strings.networks.entryPoints;

export function EntryRoutes({
  deployment,
  origins,
  onApply,
}: {
  deployment: Pick<Deployment, 'name'>;
  origins: readonly DeploymentOrigin[] | undefined;
  onApply: () => void;
}) {
  if (!origins?.length) return null;
  const waiting = origins.filter((o) => o.originState === 'waiting-for-apply');
  return (
    <div className="mt-2" data-testid="network-card-entry-routes">
      <h3 className="text-[13px] font-semibold">{e.cardTitle}</h3>
      <ul className="mt-1 space-y-1 text-[13px]">
        {origins.map((o) => {
          const warn = o.originState === 'waiting-for-apply' || o.originState === 'inactive';
          return (
            <li
              key={`${o.networkId}/${o.entryPointId}/${o.routeId}`}
              className={`flex flex-wrap items-center gap-1.5 ${warn ? 'text-[var(--warning)]' : ''}`}
            >
              {warn ? (
                <TriangleAlert aria-hidden className="size-3.5 shrink-0" />
              ) : (
                <Globe aria-hidden className="size-3.5 shrink-0 text-fg-muted" />
              )}
              <Link className="link underline" to={`/networks/${o.networkId}?tab=entry-points`}>
                {e.entryRouteLine(
                  o.routeId,
                  o.entryPointId,
                  o.resourceId,
                  // Feature spec 17, phase 5: a VPC origin reads "shop (eu-shop) → web · VPC origin".
                  o.access === 'vpc-origin' ? e.accessLabel['vpc-origin'] : o.accessText,
                  e.originState[o.originState] ?? o.originState,
                )}
              </Link>
              {o.edgeUrl && <code className="font-mono text-[12px] text-fg-muted">{o.edgeUrl}</code>}
            </li>
          );
        })}
      </ul>
      {waiting.length > 0 && (
        <p className="callout callout-warning mt-2">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            {e.applyToLock(deployment.name, [...new Set(waiting.map((o) => o.resourceId))].join(', '))}
          </span>
          <ActionButton permission="operate" className="btn-sm" onClick={onApply}>
            {strings.networks.deployment.apply}
          </ActionButton>
        </p>
      )}
    </div>
  );
}
